/**
 * Ad Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import { useGetAdAdminQuery } from '../../store/api/adsApi';
import { usePermissions } from '../../hooks/usePermissions';
import moment from 'moment';

const AdDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // Permission checks
  const canManageAds = hasPermission('manage_ads');

  const { data, isLoading, error } = useGetAdAdminQuery(Number(id!), {
    skip: !id,
  });

  const ad = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !ad) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error" variant="h6" gutterBottom>
          {t('ads.empty')}
        </Typography>
        <Button onClick={() => navigate('/ads')} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>
          {t('ads.buttons.backToAds')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={ad.title}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.ads'), path: '/ads' },
          { label: ad.title },
        ]}
        action={
          canManageAds ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/ads/${ad.id}/edit`)}
            >
              {t('ads.buttons.editAd')}
            </Button>
          ) : undefined
        }
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/ads')}
        sx={{ mb: 3 }}
      >
        {t('ads.buttons.backToAds')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {t('ads.detail.adDetails')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('ads.detail.createdOn', { date: moment(ad.created_at).format('MMMM DD, YYYY HH:mm') })}
                  </Typography>
                </Box>
                <Chip
                  label={ad.is_active ? t('ads.status.ACTIVE') : t('ads.status.INACTIVE')}
                  color={ad.is_active ? 'success' : 'default'}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.form.titleEn')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {ad.title}
                  </Typography>
                </Box>

                {ad.title_ar && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.titleAr')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {ad.title_ar}
                    </Typography>
                  </Box>
                )}

                {ad.description && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.descriptionEn')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {ad.description}
                    </Typography>
                  </Box>
                )}

                {ad.description_ar && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.descriptionAr')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {ad.description_ar}
                    </Typography>
                  </Box>
                )}

                {ad.link_url && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.linkUrl')}
                    </Typography>
                    <Typography
                      variant="body1"
                      component="a"
                      href={ad.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {ad.link_url}
                    </Typography>
                  </Box>
                )}

                {ad.link_text && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.linkTextEn')}
                    </Typography>
                    <Typography variant="body1">
                      {ad.link_text}
                    </Typography>
                  </Box>
                )}

                {ad.link_text_ar && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.form.linkTextAr')}
                    </Typography>
                    <Typography variant="body1">
                      {ad.link_text_ar}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Media Section */}
          {(ad.image || ad.video) && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                  {t('ads.detail.media')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {ad.image && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('ads.form.image')}
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Avatar
                          src={ad.image}
                          alt={ad.title}
                          variant="rounded"
                          sx={{ width: '100%', height: 'auto', maxHeight: 400, objectFit: 'contain' }}
                        >
                          <ImageIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                      </Box>
                    </Box>
                  )}

                  {ad.video && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('ads.form.video')}
                      </Typography>
                      <Box
                        sx={{
                          mt: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <video
                          src={ad.video}
                          controls
                          style={{ width: '100%', maxHeight: 400, display: 'block' }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Sidebar */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3 }}>
                {t('ads.detail.information')}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.detail.status')}
                  </Typography>
                  <Chip
                    label={ad.is_active ? t('ads.status.ACTIVE') : t('ads.status.INACTIVE')}
                    color={ad.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.form.displayOrder')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {ad.order}
                  </Typography>
                </Box>

                {ad.start_date && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.detail.startDate')}
                    </Typography>
                    <Typography variant="body1">
                      {moment(ad.start_date).format('MMMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                )}

                {ad.end_date && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('ads.detail.endDate')}
                    </Typography>
                    <Typography variant="body1">
                      {moment(ad.end_date).format('MMMM DD, YYYY HH:mm')}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.detail.views')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {ad.views_count}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.detail.clicks')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {ad.clicks_count}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.detail.createdAt')}
                  </Typography>
                  <Typography variant="body1">
                    {moment(ad.created_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('ads.detail.updatedAt')}
                  </Typography>
                  <Typography variant="body1">
                    {moment(ad.updated_at).format('MMMM DD, YYYY HH:mm')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default AdDetailPage;

